/**
 * Domain 1: Abstract Reasoning & Patterns (الاستدلال المجرد والأنماط)
 * 8 Modular Template Families generating randomized SVG geometric puzzles
 */

import { render3x3Matrix, renderHorizontalSeries, renderOptionSvg, DrawShapes } from './svg-builder.js';

export const ABSTRACT_TEMPLATES = [
  // 1. Matrix Element Count Progression (bounded strictly <= 6 with diverse ascending, descending, cyclic, and symmetric patterns)
  {
    id: 'abs_matrix_count',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const isFilled = difficulty > 1;
      const makeCell = (count) => (s) => DrawShapes.dots(count, s, isFilled, '#83C8F0');

      // Select diverse pattern modes:
      // 0: Ascending bounded <= 6
      // 1: Descending bounded in [6, 1]
      // 2: Cyclic permutation of 3 numbers
      // 3: Diagonal reflection symmetry
      const patternType = prng.pick([0, 1, 2, 3]);

      let grid = [];
      let explanation_ar = '';
      let explanation_en = '';

      if (patternType === 0) {
        // Ascending bounded so max <= 6: base in [1, 2]
        const base = prng.pick([1, 2]);
        grid = [
          [base, base + 1, base + 2],
          [base + 1, base + 2, base + 3],
          [base + 2, base + 3, base + 4]
        ];
        explanation_ar = `يزيد عدد النقاط تصاعديًا بمقدار 1 في كل صف وعمود. الصف الأخير يتبع النمط (${base + 2} ثم ${base + 3})، وبالتالي يحتوي المربع المفقود على ${base + 4} نقاط.`;
        explanation_en = `Dot counts increase monotonically by 1 across rows and columns. The bottom row follows ${base + 2}, ${base + 3}, requiring ${base + 4} dots for the target cell.`;
      } else if (patternType === 1) {
        // Descending bounded in [6, 1]
        const start = prng.pick([6, 5]);
        grid = [
          [start, start - 1, start - 2],
          [start - 1, start - 2, start - 3],
          [start - 2, start - 3, start - 4]
        ];
        explanation_ar = `يتناقص عدد النقاط تنازليًا بمقدار 1 في كل صف وعمود. الصف الأخير يبدأ بـ (${start - 2} ثم ${start - 3})، وبالتالي يحتوي المربع المفقود على ${start - 4} نقاط.`;
        explanation_en = `Dot counts decrease monotonically by 1 across rows and columns. The bottom row follows ${start - 2}, ${start - 3}, requiring ${start - 4} dots for the target cell.`;
      } else if (patternType === 2) {
        // Cyclic permutation of 3 distinct numbers from [1..6]
        const triplet = prng.sample([1, 2, 3, 4, 5, 6], 3);
        const [a, b, c] = triplet;
        grid = [
          [a, b, c],
          [b, c, a],
          [c, a, b]
        ];
        explanation_ar = `تتكرر الأعداد الثلاثة (${a}، ${b}، ${c}) في كل صف وعمود بنمط دوري لاتيني. الصف الأخير يحتوي على (${c} ثم ${a})، لذا يكتمل المربع المفقود بالعدد ${b} نقاط.`;
        explanation_en = `The three counts (${a}, ${b}, ${c}) follow a Latin square cyclic shift across rows and columns. The final row presents ${c} then ${a}, requiring ${b} dots to complete the cycle.`;
      } else {
        // Diagonal reflection symmetry
        const [d1, d2, d3, off1, off2, off3] = prng.sample([1, 2, 3, 4, 5, 6], 6);
        grid = [
          [d1, off1, off2],
          [off1, d2, off3],
          [off2, off3, d3]
        ];
        explanation_ar = `تتميز المصفوفة بتماثل انعكاسي قطري (حيث الخلية [صف, عمود] تطابق [عمود, صف]). المربع المفقود في القطر الرئيسي يكتمل بالعدد ${d3} نقاط.`;
        explanation_en = `The matrix exhibits diagonal reflection symmetry (cell [r, c] equals cell [c, r]). The target cell on the diagonal requires ${d3} dots.`;
      }

      const correctCount = grid[2][2];
      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(makeCell(grid[r][c]));
        }
      }

      // Distractors strictly bounded within [1..6] and != correctCount
      const allPossible = [1, 2, 3, 4, 5, 6].filter(n => n !== correctCount);
      const distractors = prng.sample(allPossible, 3);
      const allCounts = prng.shuffle([correctCount, ...distractors]);
      const correctIndex = allCounts.indexOf(correctCount);

      const options = allCounts.map((cnt, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(makeCell(cnt), 90, `Count ${cnt}`),
        value: cnt
      }));

      const stimulusHtml = render3x3Matrix(cells, 300);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'أي من الخيارات التالية يكمل المصفوفة المنطقية بحيث يتوافق نمط عدد النقاط أفقيًا ورأسيًا؟',
        questionText_en: 'Which option logically completes the matrix following the dot count pattern across rows and columns?',
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar,
        explanation_en
      };
    }
  },

  // 2. Matrix Shape Rotation (45° or 90° clockwise per column)
  {
    id: 'abs_matrix_rot',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const stepAngle = difficulty === 3 ? 45 : 90;
      const baseAngle = prng.int(0, 3) * 45;
      const shapeType = prng.pick(['arrow', 'triangle']);

      const drawRotated = (angle) => (s) => {
        if (shapeType === 'arrow') return DrawShapes.arrow(s, angle, '#3b82f6');
        return DrawShapes.triangle(s, true, 'currentColor', '#6666E8', angle);
      };

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const angle = (baseAngle + r * stepAngle + c * stepAngle) % 360;
          cells.push(drawRotated(angle));
        }
      }

      const correctAngle = (baseAngle + 2 * stepAngle + 2 * stepAngle) % 360;
      const possibleAngles = [0, 45, 90, 135, 180, 225, 270, 315].filter(a => a !== correctAngle);
      const distractors = prng.sample(possibleAngles, 3);
      const allAngles = prng.shuffle([correctAngle, ...distractors]);
      const correctIndex = allAngles.indexOf(correctAngle);

      const options = allAngles.map((angle, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawRotated(angle), 90, `Rotation ${angle} deg`),
        value: angle
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'ما هو الشكل الذي يكمل النمط بالدوران المناسب في المصفوفة؟',
        questionText_en: 'Which shape correctly completes the rotation pattern in the matrix?',
        stimulusHtml: render3x3Matrix(cells, 300),
        options,
        correctIndex,
        explanation_ar: `يدور الشكل في كل خطوة أفقية ورأسية بمقدار ${stepAngle} درجة في اتجاه عقارب الساعة، والزاوية الصحيحة للموضع الأخير هي ${correctAngle}°.`,
        explanation_en: `The figure rotates clockwise by ${stepAngle}° across rows and columns, arriving at ${correctAngle}° for the missing cell.`
      };
    }
  },

  // 3. Matrix Shading Sector Progression
  {
    id: 'abs_shading_rot',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const totalSlices = difficulty === 3 ? 6 : 4;
      const startSlice = prng.int(0, totalSlices - 1);

      const drawSlice = (idx) => (s) => DrawShapes.pieSlices(s, idx % totalSlices, totalSlices, '#83C8F0');

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const sliceIdx = (startSlice + r + c) % totalSlices;
          cells.push(drawSlice(sliceIdx));
        }
      }

      const correctSlice = (startSlice + 2 + 2) % totalSlices;
      const otherSlices = Array.from({ length: totalSlices }, (_, i) => i).filter(i => i !== correctSlice);
      const distractors = prng.sample(otherSlices, 3);
      const allSlices = prng.shuffle([correctSlice, ...distractors]);
      const correctIndex = allSlices.indexOf(correctSlice);

      const options = allSlices.map((sl, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawSlice(sl), 90, `Sector ${sl + 1}`),
        value: sl
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'أي من الدوائر التالية تمثل الموضع التالي للقطاع المظلل وفق تسلسل المصفوفة؟',
        questionText_en: 'Which circle represents the correct shaded sector progression in the matrix?',
        stimulusHtml: render3x3Matrix(cells, 300),
        options,
        correctIndex,
        explanation_ar: `يتحرك القطاع المظلل بمقدار موضع واحد في اتجاه عقارب الساعة مع كل خطوة، ما يجعل القطاع رقم ${correctSlice + 1} هو الإجابة الصحيحة.`,
        explanation_en: `The shaded segment shifts clockwise by one position at each step, making sector ${correctSlice + 1} the correct answer.`
      };
    }
  },

  // 4. Sequential Shape Morph Series (Linear progression 1..4, find 5)
  {
    id: 'abs_shape_morph',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Shapes by number of vertices: Triangle (3), Square (4), Pentagon (5), Hexagon (6), Heptagon (7)
      // Alternating filled vs hollow or color shift
      const alternatingFill = prng.next() > 0.5;

      const drawShapeBySides = (sides, filled) => (s) => {
        const pts = [];
        const r = s * 0.45;
        for (let i = 0; i < sides; i++) {
          const a = (i * 2 * Math.PI) / sides - Math.PI / 2;
          pts.push(`${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`);
        }
        return `<polygon points="${pts.join(' ')}" fill="${filled ? '#3b82f6' : 'none'}" stroke="currentColor" stroke-width="2.5"/>`;
      };

      const startSides = difficulty === 1 ? 3 : 4;
      const items = [];
      for (let i = 0; i < 4; i++) {
        const sides = startSides + i;
        const filled = alternatingFill ? (i % 2 === 1) : (i % 2 === 0);
        items.push(drawShapeBySides(sides, filled));
      }

      const correctSides = startSides + 4;
      const correctFilled = alternatingFill ? (4 % 2 === 1) : (4 % 2 === 0);

      // Distractors: wrong sides or wrong fill
      const distractors = [
        drawShapeBySides(correctSides, !correctFilled),
        drawShapeBySides(correctSides - 1, correctFilled),
        drawShapeBySides(correctSides + 1, correctFilled)
      ];

      const allDrawFns = prng.shuffle([drawShapeBySides(correctSides, correctFilled), ...distractors]);
      const correctIndex = allDrawFns.findIndex(fn => fn === drawShapeBySides(correctSides, correctFilled) || fn(50) === drawShapeBySides(correctSides, correctFilled)(50));

      const options = allDrawFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 80, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'ما هو الشكل التالي في هذا التسلسل الهندسي المنطقي؟',
        questionText_en: 'What is the next geometric figure in this sequential progression?',
        stimulusHtml: renderHorizontalSeries(items, 320),
        options,
        correctIndex,
        explanation_ar: `يزيد عدد أضلاع الشكل بمقدار 1 في كل خطوة (${startSides}، ${startSides + 1}، ${startSides + 2}، ${startSides + 3}) مع تناوب التظليل، لذا الشكل الخامس يتكون من ${correctSides} أضلاع ${correctFilled ? 'مظللة' : 'مفرغة'}.`,
        explanation_en: `Each step increments polygon sides by 1 (${startSides}, ${startSides + 1}, ${startSides + 2}, ${startSides + 3}) with alternating fill, requiring a ${correctSides}-sided figure that is ${correctFilled ? 'filled' : 'outlined'}.`
      };
    }
  },

  // 5. Matrix Set Operations (XOR / Union of elements)
  // 5. Matrix Set Operations (XOR / Union of elements)
  {
    id: 'abs_matrix_xor',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Lines inside cell:
      const lines = [
        (s) => `<line x1="${-s * 0.35}" y1="0" x2="${s * 0.35}" y2="0" stroke="#83C8F0" stroke-width="3"/>`,
        (s) => `<line x1="0" y1="${-s * 0.35}" x2="0" y2="${s * 0.35}" stroke="#83C8F0" stroke-width="3"/>`,
        (s) => `<line x1="${-s * 0.25}" y1="${-s * 0.25}" x2="${s * 0.25}" y2="${s * 0.25}" stroke="#83C8F0" stroke-width="3"/>`
      ];

      const makeCombo = (mask) => (s) => {
        let out = '';
        if (mask & 1) out += lines[0](s);
        if (mask & 2) out += lines[1](s);
        if (mask & 4) out += lines[2](s);
        return out || `<circle cx="0" cy="0" r="3" fill="#6666E8"/>`;
      };

      const bitPairs = [
        [1, 2], // H + V
        [2, 4], // V + Diag
        [1, 4]  // H + Diag
      ];
      const shuffledPairs = prng.shuffle(bitPairs);

      const rowMasks = shuffledPairs.map(([a, b]) => {
        const swap = prng.next() > 0.5;
        const col0 = swap ? b : a;
        const col1 = swap ? a : b;
        return [col0, col1, col0 | col1];
      });

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(makeCombo(rowMasks[r][c]));
        }
      }

      const correctMask = rowMasks[2][2];
      const possibleDistractors = [1, 2, 3, 4, 5, 6, 7].filter(m => m !== correctMask);
      const distractors = prng.sample(possibleDistractors, 3);
      const allMasks = prng.shuffle([correctMask, ...distractors]);
      const correctIndex = allMasks.indexOf(correctMask);

      const options = allMasks.map((m, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(makeCombo(m), 90, `Combination ${m}`),
        value: m
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'العمود الثالث يمثل دمج وتراكب عناصر العمودين الأول والثاني في كل صف. ما هو الشكل المتمم؟',
        questionText_en: 'The third column combines the elements of the first two columns in each row. Which shape completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 300),
        options,
        correctIndex,
        explanation_ar: 'الخلية الثالثة في كل صف تنتج عن التراكب البصري المباشر لعناصر الخليتين الأولى والثانية معاً.',
        explanation_en: 'The third cell in each row represents the visual overlay of components from the first two cells combined.'
      };
    }
  },

  // 6. Dual Property Analogy (Outer Shape + Inner Inversion)
  {
    id: 'abs_dual_property',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawNested = (outer, inner, innerFilled, rot = 0) => (s) => {
        let out = `<g transform="rotate(${rot})">`;
        if (outer === 'square') out += DrawShapes.square(s * 0.9, false, 'currentColor');
        else if (outer === 'circle') out += DrawShapes.circle(s * 0.9, false, 'currentColor');
        else if (outer === 'triangle') out += DrawShapes.triangle(s * 0.9, false, 'currentColor');
        else out += DrawShapes.diamond(s * 0.9, false, 'currentColor');

        if (inner === 'circle') out += DrawShapes.circle(s * 0.45, innerFilled, '#3b82f6', '#3b82f6');
        else if (inner === 'triangle') out += DrawShapes.triangle(s * 0.45, innerFilled, '#3b82f6', '#3b82f6');
        else if (inner === 'square') out += DrawShapes.square(s * 0.45, innerFilled, '#3b82f6', '#3b82f6');
        else out += DrawShapes.star(s * 0.45, innerFilled, '#3b82f6', '#3b82f6');

        out += '</g>';
        return out;
      };

      const outerPool = ['square', 'circle', 'diamond', 'triangle'];
      const innerPool = ['circle', 'triangle', 'square', 'star'];

      const [outerA, outerC] = prng.sample(outerPool, 2);
      const [innerA, innerC] = prng.sample(innerPool, 2);
      const rotAngle = prng.pick([45, 90]);

      const A = drawNested(outerA, innerA, false, 0);
      const B = drawNested(outerA, innerA, true, rotAngle);
      const C = drawNested(outerC, innerC, false, 0);
      const correctFn = drawNested(outerC, innerC, true, rotAngle);

      const distractors = [
        drawNested(outerC, innerC, false, rotAngle),
        drawNested(outerC, innerC, true, 0),
        drawNested(outerA, innerC, true, rotAngle)
      ];

      const allDrawFns = prng.shuffle([correctFn, ...distractors]);
      const correctIndex = allDrawFns.findIndex(fn => fn(50) === correctFn(50));

      const stimulusSvg = `<svg class="iq-svg-analogy" viewBox="0 0 320 100" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="320" height="100" rx="12" class="iq-svg-board"/>
        <g transform="translate(45, 50)">${A(60)}</g>
        <text x="85" y="55" class="iq-svg-text-muted" font-size="16" text-anchor="middle">→</text>
        <g transform="translate(125, 50)">${B(60)}</g>
        <text x="165" y="55" fill="#3b82f6" font-weight="bold" font-size="18" text-anchor="middle">::</text>
        <g transform="translate(205, 50)">${C(60)}</g>
        <text x="245" y="55" class="iq-svg-text-muted" font-size="16" text-anchor="middle">→</text>
        <rect x="265" y="25" width="50" height="50" rx="8" class="iq-svg-board-missing"/>
        <text x="290" y="56" fill="#3b82f6" font-size="20" font-weight="800" text-anchor="middle">?</text>
      </svg>`;

      const options = allDrawFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 80, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'الشكل الأول يتحول إلى الشكل الثاني وفق قاعدة محددة. ما هو الشكل الذي ينتج عن تطبيق نفس القاعدة على الشكل الثالث؟',
        questionText_en: 'The first figure transforms into the second by a specific rule. Which figure results from applying that identical rule to the third?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `القاعدة تتضمن تدوير الإطار الخارجي بمقدار ${rotAngle} درجة مع تظليل الشكل الداخلي المفرغ.`,
        explanation_en: `The transformation rule rotates the outer boundary by ${rotAngle}° while inverting the inner hollow shape into a filled one.`
      };
    }
  },

  // 7. Domino Dot Matrix Progression
  {
    id: 'abs_domino_arithmetic',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Domino split into Top and Bottom halves with dots (1 to 6)
      const drawDomino = (top, btm) => (s) => {
        const w = s * 0.7;
        const h = s * 1.1;
        let out = `<rect x="${-w / 2}" y="${-h / 2}" width="${w}" height="${h}" rx="6" class="iq-svg-board" stroke="currentColor" stroke-width="2"/>`;
        out += `<line x1="${-w / 2}" y1="0" x2="${w / 2}" y2="0" stroke="currentColor" stroke-opacity="0.35" stroke-width="1.5"/>`;
        out += `<g transform="translate(0, ${-h * 0.25})">${DrawShapes.dots(top, w * 0.8, true, '#3b82f6')}</g>`;
        out += `<g transform="translate(0, ${h * 0.25})">${DrawShapes.dots(btm, w * 0.8, true, '#6666E8')}</g>`;
        return out;
      };

      const ruleType = prng.pick([0, 1, 2]);

      let topStart = prng.int(1, 4);
      let btmStart = prng.int(3, 6);
      let topStep = prng.pick([1, 2]);
      let btmStep = prng.pick([1, -1]);

      let getCellVal;
      let expAr, expEn;

      if (ruleType === 0) {
        getCellVal = (r, c) => {
          const top = (((topStart - 1 + (r + c) * topStep) % 6) + 6) % 6 + 1;
          const btm = (((btmStart - 1 + (r + c) * btmStep) % 6) + 6) % 6 + 1;
          return { top, btm };
        };
        const correct = getCellVal(2, 2);
        expAr = `يزيد النصف العلوي بمقدار (+${topStep}) دورياً في كل خطوة (ليصل إلى ${correct.top})، بينما ${btmStep > 0 ? `يزيد النصف السفلي بمقدار (+${btmStep})` : `ينقص النصف السفلي بمقدار (${Math.abs(btmStep)})`} (ليصل إلى ${correct.btm}).`;
        expEn = `The top half advances by (+${topStep}) cyclically across steps (reaching ${correct.top}), while the bottom half ${btmStep > 0 ? `increases by (+${btmStep})` : `decreases by (${Math.abs(btmStep)})`} (reaching ${correct.btm}).`;
      } else if (ruleType === 1) {
        topStep = prng.pick([1, 2]);
        getCellVal = (r, c) => {
          const top = (((topStart - 1 + (r + c) * topStep) % 6) + 6) % 6 + 1;
          const btm = 7 - top;
          return { top, btm };
        };
        const correct = getCellVal(2, 2);
        expAr = `يتغير النصف العلوي بمقدار (+${topStep}) في كل خطوة، بينما يكمل النصف السفلي مجموع 7 نقاط في كل حجر كما في النرد القياسي، ما يجعل الحجر المطلوب هو (${correct.top} أعلى / ${correct.btm} أسفل).`;
        expEn = `The top half steps by (+${topStep}) while the bottom half always complements the top to total 7 pips (as in standard dice), requiring (${correct.top} top / ${correct.btm} bottom).`;
      } else {
        topStart = prng.int(1, 3);
        getCellVal = (r, c) => {
          const top = Math.min(6, topStart + r + c);
          const btm = Math.max(1, 6 - (r + c));
          return { top, btm };
        };
        const correct = getCellVal(2, 2);
        expAr = `يزيد النصف العلوي بمقدار 1 مع كل خلية ليصل إلى ${correct.top}، وينقص النصف السفلي ليصل إلى ${correct.btm}.`;
        expEn = `The top half increments by 1 per cell reaching ${correct.top}, while the bottom half decrements reaching ${correct.btm}.`;
      }

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const val = getCellVal(r, c);
          cells.push(drawDomino(val.top, val.btm));
        }
      }

      const target = getCellVal(2, 2);
      const correctFn = drawDomino(target.top, target.btm);

      const candidatePairs = [
        { t: target.top, b: target.btm === 6 ? 1 : target.btm + 1 },
        { t: target.top === 1 ? 6 : target.top - 1, b: target.btm },
        { t: target.btm, b: target.top },
        { t: target.top === 6 ? 1 : target.top + 1, b: target.btm === 1 ? 6 : target.btm - 1 },
        { t: target.btm === 6 ? 1 : target.btm + 1, b: target.top }
      ];
      const seenPairs = new Set([`${target.top}-${target.btm}`]);
      const uniqueDistractors = [];
      for (const p of candidatePairs) {
        const key = `${p.t}-${p.b}`;
        if (!seenPairs.has(key)) {
          seenPairs.add(key);
          uniqueDistractors.push(drawDomino(p.t, p.b));
          if (uniqueDistractors.length === 3) break;
        }
      }

      const allFns = prng.shuffle([correctFn, ...uniqueDistractors]);
      const correctIndex = allFns.findIndex(fn => fn(50) === correctFn(50));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 80, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتبع نقاط أحجار الدومينو والنرد نمطًا حسابيًا متزامنًا في النصفين العلوي والسفلي. ما هو الحجر المفقود؟',
        questionText_en: 'The domino and dice tiles follow a synchronized mathematical pattern across their halves. Which tile completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 300),
        options,
        correctIndex,
        explanation_ar: expAr,
        explanation_en: expEn
      };
    }
  },

  // 8. Nested Concentric Geometry (Size & position shifts)
  {
    id: 'abs_nested_concentric',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const outerShapes = ['circle', 'square'];
      const outerChoice = prng.pick(outerShapes);

      const rotStep = prng.pick([30, 45]);
      const innerShapePool = ['triangle', 'circle', 'star', 'diamond'];
      const chosenInnerShapes = prng.sample(innerShapePool, 3);

      const drawConcentric = (innerShape, middleRot) => (s) => {
        let out = outerChoice === 'circle' ? DrawShapes.circle(s * 0.95, false, 'currentColor') : DrawShapes.square(s * 0.95, false, 'currentColor');
        out += `<g transform="rotate(${middleRot})">${DrawShapes.square(s * 0.65, false, '#6666E8')}</g>`;
        if (innerShape === 'triangle') out += DrawShapes.triangle(s * 0.35, true, '#3b82f6', '#3b82f6');
        else if (innerShape === 'circle') out += DrawShapes.circle(s * 0.35, true, '#3b82f6', '#3b82f6');
        else if (innerShape === 'diamond') out += DrawShapes.diamond(s * 0.35, true, '#3b82f6', '#3b82f6');
        else out += DrawShapes.star(s * 0.35, true, '#3b82f6', '#3b82f6');
        return out;
      };

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const rot = (r * rotStep + c * rotStep) % 90;
          const shape = chosenInnerShapes[(r + c) % 3];
          cells.push(drawConcentric(shape, rot));
        }
      }

      const correctShape = chosenInnerShapes[(2 + 2) % 3];
      const correctRot = (2 * rotStep + 2 * rotStep) % 90;

      const correctFn = drawConcentric(correctShape, correctRot);
      const distractors = [
        drawConcentric(correctShape, (correctRot + rotStep) % 90),
        drawConcentric(chosenInnerShapes[0] === correctShape ? chosenInnerShapes[1] : chosenInnerShapes[0], correctRot),
        drawConcentric(chosenInnerShapes[2] === correctShape ? chosenInnerShapes[1] : chosenInnerShapes[2], correctRot)
      ];

      const allFns = prng.shuffle([correctFn, ...distractors]);
      const correctIndex = allFns.findIndex(fn => fn(50) === correctFn(50));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 80, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'أي من الأشكال المركبة يكمل مصفوفة الأشكال المتداخلة بصورة منطقية؟',
        questionText_en: 'Which compound figure logically completes the concentric matrix?',
        stimulusHtml: render3x3Matrix(cells, 300),
        options,
        correctIndex,
        explanation_ar: `يدور الشكل الأوسط بمقدار ${rotStep} درجة في كل خطوة، بينما يتناوب الشكل الداخلي بصورة دورية بين الأشكال المختارة.`,
        explanation_en: `The middle shape rotates by ${rotStep}° at each step, while the inner shape cycles through the designated sequence.`
      };
    }
  }
];
